import type { ReactNode } from "react"
import { useCallback, useEffect, useRef } from "react"

import type { AiChatMessage, CopilotScope } from "@productlane/lib/src/ai"
import { copilotScopeKey, sanitizeCopilotPath } from "@productlane/lib/src/ai"
import { processAiChatStream } from "@productlane/lib/src/ai-chat-sync"

import { env } from "@/env"
import { generateId } from "@/lib/id"
import {
	copilotScopeStartsFreshChat,
	isStandaloneCopilotScope,
	shouldRememberCopilotSession,
	useCopilotState,
	whenCopilotStateHydrated,
} from "@/lib/states/copilot-state"
import { useComposerState } from "../../../hooks/useComposerState"
import { aiChatRequestError, isCapReachedError } from "./ai-chat-request-error"
import { shouldSendPageContext } from "./copilot-context-pill"
import { sidebarAiChatApi } from "./sidebar-ai-chat-api"
import { shouldAdoptExistingSidebarChatState } from "./sidebar-ai-chat-hydration"
import { mcpSectionIdsForRequest } from "./sidebar-ai-chat-mcp"
import { shouldApplyAllocatedSession } from "./sidebar-ai-chat-session-alloc"
import { useSidebarAiChatState } from "./sidebar-ai-chat-state"
import { useAgentMcpSectionIds } from "./use-agent-mcp-sections"

const API_BASE = `${env.VITE_API_URL}/api/sidebar/ai-chat`

export interface CopilotPageContext {
	title?: string
	html?: string
	// Reporting copilot: the range the charts on screen are showing.
	rangeDays?: number
	rangeLabel?: string
	// Agent copilot: the recommendation open on the page.
	recommendationId?: string
}

// One stream per scope, so a turn on another thread keeps streaming into its
// cached chat instead of being cut off when the user moves on.
const activeStreamAbortControllers = new Map<string, AbortController>()

const currentScopeKey = (): string | null => {
	const { scope } = useSidebarAiChatState.getState()
	return scope ? copilotScopeKey(scope) : null
}

export const abortActiveSidebarAiChatStream = (
	scopeKey: string | null = currentScopeKey(),
) => {
	if (scopeKey === null) return
	activeStreamAbortControllers.get(scopeKey)?.abort()
	activeStreamAbortControllers.delete(scopeKey)
}

export const claimStream = (scopeKey: string | null): AbortController => {
	abortActiveSidebarAiChatStream(scopeKey)
	const controller = new AbortController()
	if (scopeKey !== null) activeStreamAbortControllers.set(scopeKey, controller)
	return controller
}

const releaseStream = (
	scopeKey: string | null,
	controller: AbortController,
) => {
	if (
		scopeKey !== null &&
		activeStreamAbortControllers.get(scopeKey) === controller
	) {
		activeStreamAbortControllers.delete(scopeKey)
	}
}

// Stop streaming but keep whatever has streamed in so far, and mark the
// assistant message processed so the input flips back to "send" and the user
// can fire off another message right away.
export const stopActiveSidebarAiChatStream = () => {
	abortActiveSidebarAiChatStream()
	const state = useSidebarAiChatState.getState()
	const messages = state.messages
	if (!messages || messages.length === 0) return
	const lastMessage = messages[messages.length - 1]
	if (lastMessage?.role !== "assistant" || lastMessage.processed) return
	state.setMessages(
		messages.map((m, i) =>
			i === messages.length - 1
				? { ...m, processed: true, waitAction: undefined }
				: m,
		),
	)
	state.setIsLoading(false)
}

export const SidebarAiChatSync = ({
	scope,
	getPageContext,
	children,
}: {
	scope: CopilotScope
	getPageContext?: () => CopilotPageContext | undefined
	children: ReactNode
}) => {
	const {
		sessionId,
		messages,
		setSessionId,
		setMessages,
		setIsLoading,
		resetForScope,
		initMessages,
		hydrateHistory,
		setIsHydratingHistory,
		isHydratingHistory,
		pendingPrompt,
		setPendingPrompt,
		pendingPostgresContinuation,
		setPendingPostgresContinuation,
	} = useSidebarAiChatState()

	const availableMcpSectionIds = useAgentMcpSectionIds()

	const scopeKey = copilotScopeKey(scope)
	const scopeThreadId = scope.kind === "thread" ? scope.threadId : null

	// Call sites pass a fresh scope literal on every render. Reading the scope
	// from a ref keeps it out of the hydration effect's deps, so a re-render
	// mid-flight no longer cancels the hydration it can no longer restart.
	const scopeRef = useRef(scope)
	scopeRef.current = scope
	const scopeThreadIdRef = useRef(scopeThreadId)
	scopeThreadIdRef.current = scopeThreadId

	const lastProcessedIdRef = useRef<string | null>(null)
	const pendingFrameRef = useRef<number | null>(null)
	const pendingMessagesRef = useRef<AiChatMessage[] | null>(null)

	const mcpSectionIdsForNextRequest = useCallback(
		() =>
			mcpSectionIdsForRequest(
				useSidebarAiChatState.getState().enabledMcpSectionIds,
				availableMcpSectionIds,
			),
		[availableMcpSectionIds],
	)

	// A streaming turn with no new user message. The server derives the prompt
	// from the continuation flag in the body.
	const runContinuationTurn = useCallback(
		(body: Record<string, unknown>, errorMessage: string) => {
			const { sessionId: requestSessionId, messages: chatHistory } =
				useSidebarAiChatState.getState()
			if (!requestSessionId || !chatHistory) return

			const aiAnswer: AiChatMessage = {
				id: generateId(),
				role: "assistant",
				content: "",
				sources: [],
				actions: [],
				processed: false,
				createdAt: new Date(),
			}

			setMessages([...chatHistory, aiAnswer])
			setIsLoading(true)

			const continuationScopeKey = currentScopeKey()
			const abortController = claimStream(continuationScopeKey)
			const isStillCurrent = () =>
				useSidebarAiChatState.getState().sessionId === requestSessionId

			const flushPendingFrame = () => {
				if (pendingFrameRef.current === null) return
				cancelAnimationFrame(pendingFrameRef.current)
				pendingFrameRef.current = null
			}

			const run = async () => {
				try {
					const response = await fetch(`${API_BASE}/${requestSessionId}`, {
						method: "POST",
						credentials: "include",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							prompt: "",
							threadId: scopeThreadIdRef.current,
							scope: scopeRef.current,
							enabledMcpSectionIds: mcpSectionIdsForNextRequest(),
							assistantMessageId: aiAnswer.id,
							currentPath: sanitizeCopilotPath(window.location.pathname),
							...body,
						}),
						signal: abortController.signal,
					})

					if (!response.ok || !response.body) {
						throw await aiChatRequestError(response, errorMessage)
					}

					await processAiChatStream({
						body: response.body,
						aiAnswer,
						chatHistory,
						onUpdate: (updatedMessages) => {
							if (!isStillCurrent()) return
							pendingMessagesRef.current = updatedMessages
							if (pendingFrameRef.current !== null) return
							pendingFrameRef.current = requestAnimationFrame(() => {
								pendingFrameRef.current = null
								const next = pendingMessagesRef.current
								pendingMessagesRef.current = null
								if (next && isStillCurrent()) setMessages(next)
							})
						},
						errorMessage,
					})
					flushPendingFrame()
					const tail = pendingMessagesRef.current
					pendingMessagesRef.current = null
					if (tail && isStillCurrent()) {
						setMessages(tail)
					}
				} catch (error) {
					if (abortController.signal.aborted) return
					if (!isStillCurrent()) return
					console.error("Sidebar AI Chat continuation error:", error)
					aiAnswer.content =
						aiAnswer.content ||
						(error instanceof Error ? error.message : errorMessage)
					aiAnswer.waitAction = undefined
					aiAnswer.processed = true
					if (isCapReachedError(error)) {
						aiAnswer.capReached = true
					}
					setMessages([...chatHistory, aiAnswer])
				} finally {
					if (!abortController.signal.aborted && isStillCurrent()) {
						setIsLoading(false)
					}
					releaseStream(continuationScopeKey, abortController)
				}
			}

			void run()
		},
		[setMessages, setIsLoading, mcpSectionIdsForNextRequest],
	)

	// Per-scope hydration. Thread scope keeps one persisted conversation per
	// thread (hydrated from the server). Workspace reopens the last remembered
	// conversation (localStorage-backed). Article and changelog keep the chat
	// they had this session (in-memory, per entry) and otherwise start fresh, so
	// each entry has its own conversation and never inherits another's.
	// Do NOT abort streams here: route changes remount this component, and
	// aborting on remount is what stopped generation mid-answer. Abort only on
	// explicit Stop / New chat / session switch / a newer send.
	const hydratedScopeRef = useRef<string | null>(null)
	useEffect(() => {
		if (hydratedScopeRef.current === scopeKey) return

		const scope = scopeRef.current

		// A focus request the user made on another thread or page must not reach
		// this scope's composer, or it steals focus and kills single-key shortcuts.
		const { pendingFocusScopeKey, consumeFocusRequest } =
			useCopilotState.getState()
		if (pendingFocusScopeKey !== null && pendingFocusScopeKey !== scopeKey) {
			consumeFocusRequest()
		}

		const existing = useSidebarAiChatState.getState()
		if (
			shouldAdoptExistingSidebarChatState({
				scope,
				storeScope: existing.scope,
				messages: existing.messages,
			})
		) {
			hydratedScopeRef.current = scopeKey
			return
		}

		hydratedScopeRef.current = scopeKey
		const restoredFromCache = resetForScope(scope)
		if (restoredFromCache) return

		if (copilotScopeStartsFreshChat(scope)) {
			useSidebarAiChatState.getState().setRequireFreshSession(true)
			initMessages()
			return
		}

		let cancelled = false
		// Hand the scope back so a later render re-runs hydration. Without this a
		// bailed-out run leaves the panel bound to a chat it never loaded.
		const releaseHydrationClaim = () => {
			const state = useSidebarAiChatState.getState()
			if (state.scope && copilotScopeKey(state.scope) === scopeKey) {
				setIsHydratingHistory(false)
			}
			if (hydratedScopeRef.current === scopeKey) {
				hydratedScopeRef.current = null
			}
		}

		const isStale = () => {
			if (cancelled) return true
			const state = useSidebarAiChatState.getState()
			return (
				state.scope === null ||
				copilotScopeKey(state.scope) !== scopeKey ||
				state.sessionId !== null ||
				state.requireFreshSession
			)
		}

		// Paint the composer now and fold the persisted conversation in when it
		// arrives, so opening the copilot never waits on a round trip.
		initMessages()
		setIsHydratingHistory(true)

		if (isStandaloneCopilotScope(scope)) {
			void (async () => {
				await whenCopilotStateHydrated()
				if (cancelled) {
					releaseHydrationClaim()
					return
				}
				const remembered = useCopilotState
					.getState()
					.getRememberedSessionId(scope)
				if (!remembered) {
					useSidebarAiChatState.getState().setRequireFreshSession(true)
					setIsHydratingHistory(false)
					return
				}
				try {
					const history = await sidebarAiChatApi.loadHistory(remembered)
					if (isStale()) {
						releaseHydrationClaim()
						return
					}
					if (history.length > 0) {
						setSessionId(remembered)
						hydrateHistory(history)
					} else {
						useCopilotState.getState().setRememberedSessionId(scope, null)
						useSidebarAiChatState.getState().setRequireFreshSession(true)
						setIsHydratingHistory(false)
					}
				} catch {
					if (isStale()) {
						releaseHydrationClaim()
						return
					}
					useCopilotState.getState().setRememberedSessionId(scope, null)
					useSidebarAiChatState.getState().setRequireFreshSession(true)
					setIsHydratingHistory(false)
				}
			})()
			return () => {
				cancelled = true
			}
		}

		if (scope.kind !== "thread") {
			useSidebarAiChatState.getState().setRequireFreshSession(true)
			setIsHydratingHistory(false)
			return
		}

		const threadId = scope.threadId

		void (async () => {
			try {
				const existingSessionId =
					await sidebarAiChatApi.findExistingSession(threadId)
				if (isStale()) {
					releaseHydrationClaim()
					return
				}
				if (!existingSessionId) {
					setIsHydratingHistory(false)
					return
				}
				const history = await sidebarAiChatApi.loadHistory(existingSessionId)
				if (isStale()) {
					releaseHydrationClaim()
					return
				}
				setSessionId(existingSessionId)
				hydrateHistory(history)
			} catch {
				if (isStale()) {
					releaseHydrationClaim()
					return
				}
				setIsHydratingHistory(false)
			}
		})()

		return () => {
			cancelled = true
		}
	}, [
		scopeKey,
		resetForScope,
		setSessionId,
		hydrateHistory,
		initMessages,
		setIsHydratingHistory,
	])

	// Reset the "last processed user message" ref whenever the active session
	// changes (new chat or pick-from-recent), so the next unprocessed user
	// message in the new session triggers a fresh stream.
	useEffect(() => {
		lastProcessedIdRef.current = null
	}, [sessionId])

	// Remember the active session so returning to the inbox overview reopens
	// that conversation.
	useEffect(() => {
		if (!sessionId || !scope || !shouldRememberCopilotSession(scope)) return
		useCopilotState.getState().setRememberedSessionId(scope, sessionId)
	}, [scope, sessionId])

	// Drain pendingPrompt (set from outside, e.g. the inbox empty-state input
	// or bottom-bar AI button) once the message list is ready to accept a new
	// user message. We don't gate this on `sessionId` — sending messages in a
	// fresh chat lazily allocates a session in the send-message effect below.
	useEffect(() => {
		if (!pendingPrompt || messages === null) return
		const lastMessage = messages[messages.length - 1]
		const isProcessing =
			lastMessage?.role === "assistant" && !lastMessage?.processed
		if (isProcessing) return
		const newMessage: AiChatMessage = {
			id: generateId(),
			role: "user",
			content: pendingPrompt,
			processed: false,
			createdAt: new Date(),
			sources: [],
			actions: [],
		}
		setMessages([...messages, newMessage])
		setPendingPrompt(null)
	}, [pendingPrompt, messages, setMessages, setPendingPrompt])

	// Watch for unprocessed user messages and send to API. If the chat
	// doesn't yet have a backing session (first message after mount or after
	// "New chat"), lazily provision one before streaming.
	useEffect(() => {
		if (!messages) return
		// A message typed while history is still loading waits for the session id
		// that hydration is about to bring, instead of provisioning a second one.
		if (isHydratingHistory) return

		const lastMessage = messages[messages.length - 1]
		if (
			!lastMessage ||
			lastMessage.role !== "user" ||
			lastMessage.processed ||
			lastMessage.id === lastProcessedIdRef.current
		) {
			return
		}

		lastProcessedIdRef.current = lastMessage.id

		const processedMessage: AiChatMessage = {
			...lastMessage,
			processed: true,
		}

		const chatHistory = messages.map((m) =>
			m.id === lastMessage.id ? processedMessage : m,
		)

		const aiAnswer: AiChatMessage = {
			id: generateId(),
			role: "assistant",
			content: "",
			sources: [],
			actions: [],
			processed: false,
			createdAt: new Date(),
		}

		setMessages([...chatHistory, aiAnswer])
		setIsLoading(true)

		const streamScopeKey = scopeKey
		const abortController = claimStream(streamScopeKey)

		// requestSessionId is captured below once we know it (either the
		// current sessionId or a freshly-provisioned one). The "still
		// current" check uses it to drop updates when the user starts a new
		// chat or picks a different session mid-stream.
		let requestSessionId: string | null = sessionId
		const isStillCurrent = () =>
			requestSessionId !== null &&
			useSidebarAiChatState.getState().sessionId === requestSessionId

		const publishMessages = (
			updatedMessages: AiChatMessage[],
			isLoading: boolean,
		) => {
			const chatState = useSidebarAiChatState.getState()
			if (isStillCurrent()) {
				chatState.setMessages(updatedMessages)
				if (!isLoading) chatState.setIsLoading(false)
				return
			}
			chatState.patchCachedScopeChat(streamScopeKey, {
				sessionId: requestSessionId,
				messages: updatedMessages,
				isLoading,
			})
		}

		const run = async () => {
			try {
				if (!requestSessionId) {
					const requireFresh =
						useSidebarAiChatState.getState().requireFreshSession
					const allocated = await sidebarAiChatApi.createSession({
						fresh: requireFresh,
						scope,
					})
					if (abortController.signal.aborted) return
					const state = useSidebarAiChatState.getState()
					if (
						!shouldApplyAllocatedSession({
							allocated,
							streamScopeKey,
							allocatedWithFresh: requireFresh,
							state,
						})
					) {
						return
					}
					requestSessionId = allocated
					setSessionId(allocated)
					if (requireFresh) {
						useSidebarAiChatState.getState().setRequireFreshSession(false)
					}
				}

				const editorContent =
					scopeThreadId != null
						? useComposerState.getState().entries[scopeThreadId]?.content ||
							null
						: null

				const pageContextDismissed =
					useSidebarAiChatState.getState().pageContextDismissed
				const pageContext = shouldSendPageContext(scope, pageContextDismissed)
					? getPageContext?.()
					: undefined

				const response = await fetch(`${API_BASE}/${requestSessionId}`, {
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						prompt: processedMessage.content,
						editorContent,
						threadId: scopeThreadId,
						scope,
						userMessageId: processedMessage.id,
						assistantMessageId: aiAnswer.id,
						pageContext: pageContext ?? null,
						pageContextDismissed,
						attachedContext: processedMessage.attachedContext ?? [],
						enabledMcpSectionIds: mcpSectionIdsForNextRequest(),
						currentPath: sanitizeCopilotPath(window.location.pathname),
					}),
					signal: abortController.signal,
				})

				if (!response.ok || !response.body) {
					throw await aiChatRequestError(
						response,
						"Failed to fetch AI response",
					)
				}

				await processAiChatStream({
					body: response.body,
					aiAnswer,
					chatHistory,
					onUpdate: (updatedMessages) => {
						pendingMessagesRef.current = updatedMessages
						if (pendingFrameRef.current !== null) return
						pendingFrameRef.current = requestAnimationFrame(() => {
							pendingFrameRef.current = null
							const next = pendingMessagesRef.current
							pendingMessagesRef.current = null
							if (next) publishMessages(next, true)
						})
					},
					errorMessage: "Failed to fetch AI response",
				})
				if (pendingFrameRef.current !== null) {
					cancelAnimationFrame(pendingFrameRef.current)
					pendingFrameRef.current = null
				}
				const tail = pendingMessagesRef.current
				pendingMessagesRef.current = null
				if (tail) publishMessages(tail, false)
			} catch (error) {
				if (abortController.signal.aborted) return
				console.error("Sidebar AI Chat error:", error)
				const message =
					error instanceof Error ? error.message : "Failed to fetch AI response"
				aiAnswer.content = aiAnswer.content || message
				aiAnswer.waitAction = undefined
				aiAnswer.processed = true
				if (isCapReachedError(error)) {
					aiAnswer.capReached = true
				}
				publishMessages([...chatHistory, aiAnswer], false)
			} finally {
				if (!abortController.signal.aborted) {
					if (isStillCurrent()) {
						setIsLoading(false)
					} else {
						useSidebarAiChatState
							.getState()
							.patchCachedScopeChat(streamScopeKey, {
								sessionId: requestSessionId,
								isLoading: false,
							})
					}
				}
				releaseStream(streamScopeKey, abortController)
			}
		}

		void run()
	}, [
		messages,
		sessionId,
		isHydratingHistory,
		setMessages,
		setSessionId,
		setIsLoading,
		scope,
		scopeKey,
		scopeThreadId,
		getPageContext,
		mcpSectionIdsForNextRequest,
	])

	// Postgres approval follow-up. After the user clicks "Run query" and the
	// execute mutation returns, the approve handler stashes the pending action
	// id here. We POST a continuation turn (no user message, just a request
	// for the model to interpret the cached result) and append a new streaming
	// assistant bubble below the approval card.
	useEffect(() => {
		if (!pendingPostgresContinuation || !sessionId || !messages) return

		const lastMessage = messages[messages.length - 1]
		if (lastMessage?.role === "assistant" && !lastMessage.processed) return

		const pendingActionId = pendingPostgresContinuation
		setPendingPostgresContinuation(null)
		runContinuationTurn(
			{ continueFromPendingAction: pendingActionId },
			"Failed to fetch follow-up response",
		)
	}, [
		pendingPostgresContinuation,
		sessionId,
		messages,
		setPendingPostgresContinuation,
		runContinuationTurn,
	])

	return children
}
